export default function StopSayingJustHi() {
  return (
    <article className="prose">
      <p>
        "Hi"
      </p>
      
      <p>
        That's it. That's the entire message. No context. No question. No reason for reaching out. Just "hi" 
        sitting in my inbox, waiting for me to respond so you can then tell me what you actually want.
      </p>

      <p>
        I understand this comes from a place of politeness, but there's a more effective way to communicate 
        that respects everyone's time.
      </p>

      <h2>Why "Hi" Can Be Inefficient</h2>
      <p>
        When you send just "hi" and wait for a response, you're essentially asking me to:
      </p>

      <ol>
        <li>Stop what I'm doing</li>
        <li>Context switch to check your message</li>
        <li>Respond with "hi" back</li>
        <li>Wait for you to type out what you actually want</li>
        <li>Context switch again to read your actual message</li>
        <li>Finally understand what you need and respond</li>
      </ol>

      <p>
        That's <strong>two unnecessary context switches</strong> and a bunch of wasted time. You've turned 
        what could have been a single, efficient interaction into a drawn-out back-and-forth.
      </p>

      <h2>Common Scenarios</h2>
      <p>
        I notice this pattern frequently across different types of professional communication:
      </p>

      <ul>
        <li><strong>Recruiters:</strong> "Hi" (then wait) "I have an opportunity for you"</li>
        <li><strong>Candidates:</strong> "Hi" (wait) "Can you refer me?"</li>
        <li><strong>Developers:</strong> "Hi" (wait) "I have a question about your code"</li>
        <li><strong>LinkedIn connections:</strong> "Hi" (wait) "Can we connect?"</li>
      </ul>

      <p>
        Since we're already connected on these platforms, we can skip the greeting formality and get straight 
        to the purpose of the message.
      </p>

      <h2>A More Effective Approach</h2>
      <p>
        I understand that starting with "hi" feels polite and professional. However, in asynchronous 
        communication, being clear and direct is actually more respectful of everyone's time.
      </p>

      <p>
        Professional communication isn't a casual chat at a coffee shop. It's asynchronous. The whole point 
        is that I can read your message when I have time and respond thoughtfully. But when you send just "hi," 
        you're forcing synchronous communication where it doesn't need to exist.
      </p>

      <h2>What You Should Do Instead</h2>
      <p>
        It's simple. Just say what you want in your first message:
      </p>

      <p>
        <strong>Bad:</strong><br />
        "Hi"<br />
        (wait for response)<br />
        "Can you refer me to your company?"
      </p>

      <p>
        <strong>Good:</strong><br />
        "Hi Ravindra, I noticed you work at [Company]. I'm interested in the [Position] role and was wondering 
        if you'd be open to discussing it. I have [X years] of experience in [relevant skills]. Would you be 
        willing to chat about the role and team?"
      </p>

      <p>
        See the difference? The second message:
      </p>
      <ul>
        <li>States the purpose clearly</li>
        <li>Provides context</li>
        <li>Shows you've done your homework</li>
        <li>Respects my time by letting me respond when convenient</li>
        <li>Gives me enough information to decide if I can help</li>
      </ul>

      <h2>Read This: nohello.net</h2>
      <p>
        I'm not the only one who feels this way. There's an entire website dedicated to this issue: 
        <a href="https://nohello.net/en/" target="_blank" rel="noopener noreferrer">
          <strong>nohello.net</strong>
        </a>
      </p>

      <p>
        Go read it. Seriously. It explains this concept better than I can, and it's been around for years 
        because this is a widespread problem in professional communication.
      </p>

      <h2>Tips for Recruiters</h2>
      <p>
        When reaching out about job opportunities, including key details upfront helps candidates quickly 
        assess if it's a good fit. Consider including:
      </p>

      <ul>
        <li>Company name (or at least the industry if it's confidential)</li>
        <li>Role title and level</li>
        <li>Location/remote policy</li>
        <li>Rough salary range</li>
        <li>Why you think it might be a good fit</li>
      </ul>

      <p>
        This information helps both parties save time and ensures better quality conversations.
      </p>

      <h2>Tips for Reaching Out About Companies</h2>
      <p>
        If you're interested in learning about a company or team where I've worked in the past or currently work, 
        being specific about what you'd like to know helps facilitate a more meaningful conversation. For example:
      </p>

      <ul>
        <li>Introduce yourself and your background</li>
        <li>Explain what specifically interests you about the company or team</li>
        <li>Ask specific questions about the work culture, tech stack, or team dynamics</li>
        <li>Share what you're hoping to learn from the conversation</li>
      </ul>

      <p>
        Note that I only refer people I've actually worked with (as I explain in 
        <a href="/blog/why-i-dont-refer-strangers">another post</a>), 
        but I'm happy to share insights about the company and team with anyone genuinely interested.
      </p>

      <h2>Tips for Technical Questions</h2>
      <p>
        When asking technical questions about code, projects, or blog posts, providing context helps get 
        better answers faster. Include:
      </p>

      <ul>
        <li>What you're trying to do</li>
        <li>What you've already tried</li>
        <li>The specific error or issue you're facing</li>
        <li>Relevant code snippets or links</li>
      </ul>

      <p>
        This approach often leads to more helpful and detailed responses.
      </p>

      <h2>The Benefits</h2>
      <p>
        Both your time and mine are valuable. By being clear and direct from the start, 
        both of us benefit from more efficient communication.
      </p>

      <p>
        Next time you're about to send just "hi" to someone, consider including your actual question or 
        request in that first message. Write it out clearly and concisely.
      </p>

      <p>
        This approach typically results in faster responses and more productive conversations. You'll come 
        across as thoughtful and respectful of my time.
      </p>

      <p>
        For more on this topic, check out <a href="https://nohello.net/en/" target="_blank" rel="noopener noreferrer">nohello.net</a>, 
        which explains this communication principle in detail.
      </p>
    </article>
  );
}
